import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, Platform, TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiPost } from '../../src/api/client';
import { useTheme } from '../../src/context/ThemeContext';
import ContentContainer from '../../src/components/ContentContainer';
import InlineMessage from '../../src/components/InlineMessage';
import QRScannerModal from '../../src/components/QRScannerModal';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useResponsive, rs, fs } from '../../src/utils/responsive';
import PageFooter from '../../src/components/PageFooter';

interface Controller {
  id: string;
  name: string | null;
  firmwareVersion: string | null;
  isOnline: boolean;
  lastSeen: string | null;
  bayId: string | null;
  bayName: string | null;
  locationName: string | null;
  otaStatus: string | null;
  otaVersion: string | null;
}

/** OwnerControllers - list and claim ESP32 controllers. */
export default function OwnerControllers() {
  const { colors } = useTheme();
  const { isDesktopWeb } = useResponsive();
  const styles = useStyles(colors);
  const router = useRouter();
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimToken, setClaimToken] = useState('');
  const [showClaimInput, setShowClaimInput] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchControllers = useCallback(async (signal?: AbortSignal) => {
    try {
      const data = await api<Controller[]>('/api/controllers');
      if (signal?.aborted) return;
      setControllers(data);
    } catch (err: any) {
      if (!(signal?.aborted)) console.error('Failed to load controllers:', err);
    } finally {
      if (!(signal?.aborted)) setLoading(false);
    }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchControllers(ac.signal); return () => ac.abort(); }, []);

  const handleClaim = async () => {
    if (!claimToken.trim()) return;
    setClaiming(true);
    try {
      await apiPost('/api/controllers/claim', { controllerId: claimToken.trim() });
      setShowClaimInput(false);
      setClaimToken('');
      fetchControllers();
      setInlineMsg({ type: 'success', message: 'Controller claimed successfully!' });
    } catch (err: any) {
      setInlineMsg({ type: 'error', message: err.message || 'Failed to claim controller' });
    } finally {
      setClaiming(false);
    }
  };

  const handleQrScanned = async (data: string) => {
    setShowScanner(false);
    const match = data.match(/rinsepay:\/\/claim\/([a-zA-Z0-9-]+)/);
    if (match) {
      setClaiming(true);
      try {
        await apiPost('/api/controllers/claim', { controllerId: match[1] });
        setShowClaimInput(false);
        setClaimToken('');
        fetchControllers();
        setInlineMsg({ type: 'success', message: 'Controller claimed successfully!' });
      } catch (err: any) {
        setInlineMsg({ type: 'error', message: err.message || 'Failed to claim controller' });
      } finally {
        setClaiming(false);
      }
    } else {
      // Not a valid RinsePay URL — put raw data in token input for manual review
      setClaimToken(data);
      setShowClaimInput(true);
    }
  };

  const renderController = ({ item }: { item: Controller }) => (
    <TouchableOpacity
      style={styles.card}
      onPress={() => router.push(`/owner/controllers/${item.id}` as never)}
      activeOpacity={0.7}
    >
      <View style={styles.cardHeader}>
        <View style={styles.cardLeft}>
          <View style={[styles.statusDot, { backgroundColor: item.isOnline ? '#059669' : '#dc2626' }]} />
          <Text style={styles.cardName}>
            {item.name || item.id}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
      </View>

      <View style={styles.cardBody}>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Status</Text>
          <Text style={[styles.infoValue, { color: item.isOnline ? '#059669' : '#dc2626' }]}>
            {item.isOnline ? 'Online' : 'Offline'}
          </Text>
        </View>
        {item.firmwareVersion && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Firmware</Text>
            <Text style={styles.infoValue}>{item.firmwareVersion}</Text>
          </View>
        )}
        {item.bayName && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Bay</Text>
            <Text style={styles.infoValue}>{item.bayName}</Text>
          </View>
        )}
        {item.locationName && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Location</Text>
            <Text style={styles.infoValue} numberOfLines={1}>{item.locationName}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );

  return (
    <ContentContainer role="owner" footer={false}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Controllers</Text>
          <TouchableOpacity
            style={styles.claimBtn}
            onPress={() => setShowClaimInput(!showClaimInput)}
          >
            <Ionicons name="add-circle" size={20} color="#fff" />
            <Text style={styles.claimBtnText}>Claim Controller</Text>
          </TouchableOpacity>
        </View>

        {showClaimInput && (
          <View style={styles.claimCard}>
            <Text style={styles.claimTitle}>Add Controller</Text>
            <Text style={styles.claimHint}>
              Scan the QR code on the controller or enter the controller ID manually.
            </Text>
            <View style={styles.claimInputRow}>
              <View style={styles.inputWrap}>
                <TextInput
                  style={styles.claimInput}
                  placeholder="e.g. RPC-A1B2C3D4"
                  placeholderTextColor={colors.textLight}
                  value={claimToken}
                  onChangeText={setClaimToken}
                  autoCapitalize="none"
                  autoCorrect={false}
                  maxLength={64}
                />
              </View>
              {!(Platform.OS === 'web' && isDesktopWeb) && (
                <TouchableOpacity
                  style={styles.cameraScanBtn}
                  onPress={() => setShowScanner(true)}
                >
                  <Ionicons name="camera" size={22} color={colors.primary} />
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.claimSubmitBtn, claiming && { opacity: 0.6 }]}
                onPress={handleClaim}
                disabled={claiming}
              >
                {claiming ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.claimSubmitText}>Claim</Text>
                )}
              </TouchableOpacity>
            </View>
            {Platform.OS === 'web' && isDesktopWeb && (
              <View style={styles.webCameraNote}>
                <Ionicons name="phone-portrait-outline" size={14} color="#dc2626" />
                <Text style={styles.webCameraText}>QR scanning available on mobile app</Text>
              </View>
            )}
          </View>
        )}

        {inlineMsg && (
          <InlineMessage
            type={inlineMsg.type}
            message={inlineMsg.message}
            visible={!!inlineMsg}
            onDismiss={() => setInlineMsg(null)}
          />
        )}

        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : (
          <FlatList
            ListFooterComponent={<PageFooter />}
            data={controllers}
            keyExtractor={(item) => item.id}
            renderItem={renderController}
            contentContainerStyle={{ paddingBottom: spacing.xl }}
            // Banners live in the list header so they scroll away with the
            // content instead of pinning above the list and eating phone space.
            ListHeaderComponent={
              <>
                <TouchableOpacity
                  style={styles.shopBanner}
                  onPress={() => router.push('/owner/shop' as never)}
                  activeOpacity={0.7}
                >
                  <View style={styles.shopBannerLeft}>
                    <Ionicons name="cart" size={22} color="#fff" />
                    <View>
                      <Text style={styles.shopBannerTitle}>Need Hardware?</Text>
                      <Text style={styles.shopBannerSub}>Shop controllers & view installation guides</Text>
                    </View>
                  </View>
                  <Ionicons name="chevron-forward" size={20} color="#fff" />
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.guideBanner}
                  onPress={() => router.push('/owner/guides/installation' as never)}
                  activeOpacity={0.7}
                >
                  <View style={styles.shopBannerLeft}>
                    <Ionicons name="book" size={22} color={colors.primary} />
                    <View>
                      <Text style={[styles.shopBannerTitle, { color: colors.text }]}>Installation Guide</Text>
                      <Text style={[styles.shopBannerSub, { color: colors.textSecondary }]}>Setup instructions for all wash types</Text>
                    </View>
                  </View>
                  <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
                </TouchableOpacity>
              </>
            }
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <Ionicons name="hardware-chip-outline" size={64} color={colors.textLight} />
                <Text style={styles.emptyTitle}>No Controllers</Text>
                <Text style={styles.emptyText}>
                  Add a controller by scanning its QR code or entering its ID to get started.
                </Text>
              </View>
            }
          />
        )}
      </View>
      <QRScannerModal
        visible={showScanner}
        onClose={() => setShowScanner(false)}
        onScanned={handleQrScanned}
      />
    </ContentContainer>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  shopBanner: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: c.primary, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md,
  },
  guideBanner: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md, borderWidth: 1, borderColor: c.borderLight,
  },
  shopBannerLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
  shopBannerTitle: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  shopBannerSub: { color: 'rgba(255,255,255,0.85)', fontSize: fontSize.xs },
  claimBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md,
  },
  claimBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  claimCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md, borderWidth: 1, borderColor: c.borderLight,
  },
  claimTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  claimHint: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.sm },
  claimInputRow: { flexDirection: 'row', gap: spacing.sm, alignItems: 'center' },
  inputWrap: { flex: 1 },
  claimInput: {
    flex: 1, padding: rs(10), fontSize: fs(14), borderWidth: 1, borderColor: c.borderLight,
    borderRadius: 8, backgroundColor: c.surface, color: c.text, fontFamily: 'monospace',
  },
  cameraScanBtn: {
    width: 44, height: 44, justifyContent: 'center', alignItems: 'center',
    borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.md,
    backgroundColor: c.surface,
  },
  claimSubmitBtn: {
    backgroundColor: c.primary, paddingHorizontal: spacing.lg, paddingVertical: rs(10),
    borderRadius: borderRadius.md,
  },
  claimSubmitText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  webCameraNote: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm,
  },
  webCameraText: { fontSize: fontSize.xs, color: '#dc2626' },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: c.borderLight,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  cardLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
  statusDot: { width: 10, height: 10, borderRadius: 5 },
  cardName: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.text, flex: 1 },
  cardBody: { gap: 4 },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between' },
  infoLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  infoValue: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium },
  emptyState: { alignItems: 'center', paddingTop: rs(80) },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', maxWidth: 300, marginTop: spacing.xs },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
